import React, { useMemo } from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';
import {
  BoardTypeDto,
  ContractorResourceTypeEnum,
} from '@vatos-pas/common';

import {
  CheckboxGroup,
  FormToolbar,
  SnackbarContainer,
} from 'components/standalone';
import { createValidationResolver } from 'utils';
import { BoardFormValues } from '../containers';
import { validateBoard } from '../views/boardValidation';

export interface BoardFormProps {
  mode: 'create' | 'edit';
  initialValues?: Partial<BoardTypeDto>;
  existingBoards: BoardTypeDto[];
  currentBoardId?: string | number;
  onSubmit: (values: BoardFormValues) => Promise<void>;
  loading: boolean;
  onGoBack: () => void;
}

const payableResourceTypeOptions = Object.values(
  ContractorResourceTypeEnum,
).map(resourceType => ({
  id: resourceType,
  name: resourceType,
}));

const contractorResourceTypeValues = new Set<string>(
  Object.values(ContractorResourceTypeEnum),
);

const isContractorResourceType = (
  value: string,
): value is ContractorResourceTypeEnum =>
  contractorResourceTypeValues.has(value);

const getDefaultValues = (
  initialValues?: Partial<BoardTypeDto>,
): BoardFormValues => ({
  name: initialValues?.name || '',
  shortName: initialValues?.shortName || '',
  externalId: initialValues?.externalId || '',
  payableResourceTypes: initialValues?.payableResourceTypes
    ? [...initialValues.payableResourceTypes]
    : [],
  laborOnly: initialValues?.laborOnly ?? false,
});

export const BoardForm: React.FC<BoardFormProps> = ({
  mode,
  initialValues,
  existingBoards,
  currentBoardId,
  onSubmit,
  loading,
  onGoBack,
}) => {
  const classes = useStyles();
  const isCreateMode = mode === 'create';
  const { notify } = SnackbarContainer.useContainer();

  const formValues = useMemo(
    () => (initialValues ? getDefaultValues(initialValues) : undefined),
    [initialValues],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<BoardFormValues>({
    defaultValues: getDefaultValues(initialValues),
    values: formValues,
    resetOptions: { keepDirtyValues: true },
    resolver: createValidationResolver<BoardFormValues>(values =>
      validateBoard(values, existingBoards, currentBoardId),
    ),
    mode: 'onBlur',
  });

  const onFormSubmit = useMemo(
    () =>
      handleSubmit(
        async data => {
          await onSubmit(data);
        },
        () => {
          notify('The form is not valid. Please check for errors', 'error');
        },
      ),
    [handleSubmit, onSubmit, notify],
  );

  return (
    <Box>
      <Box className={classes.formContent}>
        <Box className={classes.fields}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={field.name}
                label="Board Name"
                variant="filled"
                margin="dense"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
          <Controller
            name="shortName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={field.name}
                label="Board Shortname"
                variant="filled"
                margin="dense"
                error={!!errors.shortName}
                helperText={errors.shortName?.message}
              />
            )}
          />
          <Controller
            name="externalId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={field.name}
                label="External ID"
                variant="filled"
                margin="dense"
                error={!!errors.externalId}
                helperText={errors.externalId?.message}
              />
            )}
          />
        </Box>

        <Box className={classes.fullBox}>
          <Controller
            name="payableResourceTypes"
            control={control}
            render={({ field }) => (
              <CheckboxGroup
                label="Payable Resource Types"
                options={payableResourceTypeOptions}
                value={field.value}
                onChange={newValue =>
                  field.onChange(newValue.filter(isContractorResourceType))
                }
                error={errors.payableResourceTypes?.message}
                row
                data-cy="payableResourceTypes"
              />
            )}
          />
        </Box>

        {isCreateMode && (
          <Box className={classes.fullBox}>
            <Typography>Visible to Labor Only</Typography>
            <Controller
              name="laborOnly"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={field.value}
                      onChange={(_event, checked) => field.onChange(checked)}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={field.ref}
                      data-cy="laborOnly"
                    />
                  }
                  label="Labor Only"
                />
              )}
            />
          </Box>
        )}
      </Box>

      <FormToolbar
        onGoBack={onGoBack}
        onSave={onFormSubmit}
        saveLoading={loading}
        showSave
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  formContent: {
    padding: theme.spacing(2),
  },
  fields: {
    width: '100%',
    display: 'flex',
    gap: 16,
  },
  fullBox: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
}));

export default BoardForm;
